import type { FC } from "react"
import React from "react"

import type { InstallArea } from "@logic/lib/design"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import {
  DefaultValue,
  selector,
  selectorFamily,
  useRecoilState,
  useRecoilValue,
} from "recoil"
import styled, { css } from "styled-components"

import { slopeSelectOptions } from "~/containers/Proposal/state/designs/installAreas"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import { fontWeight } from "~/utils/styles"

import { findAreaState } from "../state/elements"
import { readonlyTransactors } from "../state/elements/transactions"
import { checkWhetherAreasAreEditable } from "../state/elements/transactions/checkWhetherAreasAreEditable"

const Container = styled.div<{ blocked?: boolean }>`
  padding: 0 12px;
  select,
  label,
  input {
    font-size: 14px;
    line-height: 14px;
    ${fontWeight(500)}
    &:disabled {
      color: var(--grey-font);
      cursor: not-allowed;
    }
  }
  input[type="number"],
  label[data-role="input"] {
    color: var(--blue-dark-font);
    border: 1px solid var(--grey-border);
    border-radius: 4px;
    padding: 6px;
    margin-bottom: 20px;
    text-align: right;
    appearance: none;
    width: 100%;
    flex-grow: 1;
  }
  label {
    color: var(--grey-dark);
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    text-transform: capitalize;
    margin-bottom: 5px;
  }

  ${({ blocked }) =>
    blocked &&
    css`
      * {
        cursor: not-allowed;
      }
    `}
`

const ItemWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 5px;
  margin-top: 10px;
  flex-shrink: 1;
  flex-grow: 1;
  input[type="number"] {
    max-width: 75px;
  }
`

const SlopeWrapper = styled(ItemWrapper)`
  > span {
    display: flex;
    flex-direction: row;
    gap: 10px;
    > label[data-role="input"] {
      flex-grow: 1;
      width: 100%;
      select {
        flex-grow: 1;
        width: 100%;
        margin-right: 10px;
      }
    }
    input[type="number"] {
      width: 60px;
      text-align: right;
    }
  }
`

const SliderInput = styled.input`
  margin-bottom: 10px;
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 5px;
  background: #d3d3d3;
  outline: none;
  opacity: 0.7;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
  &:disabled {
    ::-webkit-slider-thumb {
      cursor: not-allowed;
      background: #d3d3d3;
    }
  }
  ::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: var(--blue);
    cursor: pointer;
  }
`

const SunroofToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 5px;
  margin-bottom: 10px;
  label {
    width: fit-content;
    height: fit-content;
  }
  input {
    transform: translateY(-3px);
  }
  input[type="number"] {
    margin-bottom: 0;
    max-width: 50px;
  }
  p {
    font-size: 14px;
    font-style: italic;
  }
`

export const findPhaseGuardedAreaState = selectorFamily<InstallArea, string>({
  key: `findPhaseGuardedArea`,
  get:
    (areaId: string) =>
    ({ get }) =>
      get(findAreaState(areaId)),
  set: (areaId: string) => (transactors, newValue) => {
    const areasAreEditable = checkWhetherAreasAreEditable(transactors)
    if (areasAreEditable) {
      const { set, reset } = transactors

      if (newValue instanceof DefaultValue) {
        reset(findAreaState(areaId))
      }
      set(findAreaState(areaId), newValue)
    }
  },
})

export const canEditAreasState = selector<boolean>({
  key: `canEditAreas`,
  get: ({ get }) => {
    const areasAreEditable = checkWhetherAreasAreEditable(
      readonlyTransactors(get),
    )
    const currentUserIsDesignUser = get(currentUserIsDesignRequestUserState)

    return areasAreEditable && currentUserIsDesignUser
  },
})

const AreaOptions_Internal: FC<{ id: string }> = ({ id }) => {
  const canEditAreas = useRecoilValue(canEditAreasState)
  const [area, setArea] = useRecoilState(findPhaseGuardedAreaState(id))
  const changeArea = (delta: Partial<InstallArea>) =>
    setArea((current) => ({ ...current, ...delta }))

  const { t } = useTranslation()

  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  const shading = area.manualShading ?? 0

  return (
    <Container
      blocked={!canEditAreas}
      onClick={!currentUserIsDesignUser ? triggerPreventionToast : undefined}
    >
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden">
          <SlopeWrapper>
            <label htmlFor="slope">{t(`slope`)}</label>
            <span>
              <label data-role="input" htmlFor="slope">
                <select
                  id="slope"
                  disabled={!canEditAreas}
                  value={area?.slope || 0}
                  onChange={(e) => changeArea({ slope: Number(e.target.value) })}
                >
                  {slopeSelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label.charAt(0).toUpperCase() +
                        option.label.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
              <input
                type="number"
                disabled={!canEditAreas}
                value={area.slope}
                onChange={(e) => changeArea({ slope: Number(e.target.value) })}
              />
            </span>
          </SlopeWrapper>
        </span>
        <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden">
          <ItemWrapper>
            <label htmlFor="azimuth">{t(`azimuth`)}</label>
            <input
              id="azimuth"
              name="azimuth"
              type="number"
              disabled={!canEditAreas}
              step="1"
              value={
                typeof area.azimuth === `number`
                  ? Math.round(
                      area.azimuth > 359
                        ? 359
                        : area.azimuth < 0
                          ? 0
                          : area.azimuth,
                    )
                  : ``
              }
              onChange={(e) =>
                changeArea({
                  azimuth:
                    e.target.value === ``
                      ? null
                      : Number(e.target.value) > 359
                        ? 359
                        : Number(e.target.value) < 0
                          ? 0
                          : Number(e.target.value),
                })
              }
            />
          </ItemWrapper>
        </span>
        <span className="flex flex-col justify-between items-start gap-[30px] min-h-[55px] relative min-[750px]:flex-row min-[750px]:gap-y-0 [&>div]:w-full [&>div]:overflow-hidden">
          <ItemWrapper>
            <div>
              <label htmlFor="shading">{t(`shading`)}</label>
              <SliderInput
                type="range"
                min="0"
                step="1"
                max="100"
                disabled={!canEditAreas}
                value={shading}
                onChange={({ target: { value } }) => {
                  const val = Number(value)
                  if (!isNaN(val) && val >= 0 && val <= 100) {
                    changeArea({ manualShading: val })
                  }
                }}
              />
              <input
                id="shading"
                name="shading"
                type="number"
                step="1"
                min="0"
                max="100"
                value={shading}
                disabled={!canEditAreas}
                onChange={({ target: { value } }) => {
                  const val = Number(value)
                  if (!isNaN(val) && val >= 0 && val <= 100) {
                    changeArea({ manualShading: val })
                  }
                }}
              />
            </div>
          </ItemWrapper>
        </span>
      </motion.div>
    </Container>
  )
}
export const AreaOptions = React.memo(AreaOptions_Internal)
